/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */

import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import TagManager from 'react-gtm-module';
import _get from 'lodash.get';
import _pick from 'lodash.pick';
import Auth0 from 'auth0-js';
import { datadogRum } from '@datadog/browser-rum-slim';
import JsCookies from 'js-cookie';

import PasswordReset from '../../services/password-reset';
import Env from '../../services/environment';
import { DEFAULT_TNC } from '../../constants';
import { CLIENT_COLOR_NAMES, SE_NAMES, SE_ADDITIONAL_LEGALS } from '../../constants/clients';

const getEnv = Env.name() === 'production' ? 'PROD' : 'UAT';
const EnvVars = Env.vars();

export const Auth0Context = React.createContext();

export const useAuth0 = () => useContext(Auth0Context);

const getErrorFromDesc = (error) => (error.description ? new Error(error.description) : error);

const SINGPASS_CLIENT_ID = EnvVars.SINGPASS_PROXY_CLIENT_ID;
const SINGPASS_REDIRECT_URL = `${EnvVars.AUTH_URL}/login/callback`;

const USER_CONTEXT_NO_RESET = ['authRef', 'clientBasedUi', 'isSingpassLogin', 'isSingpassReAuth'];
const COOKIE_REDIRECT_ERROR_SHOWN_KEY = 'redirect_err_shown';

const Auth0Provider = ({ children, auth0Config: auth0ConfigProps, auth0RPConfig }) => {
  const [auth0Client, setAuth0Client] = useState();
  /* use isLoading and setIsLoading for API calls
  on <Button /> loading prop
  which is not fired by Formik onSubmit()
  for Formik calls, we use isSubmitting and setSubmitting
  provided by Formik */
  const [isLoading, setIsLoading] = useState();
  const [databaseConnection, setDatabaseConnection] = useState('NE-Digital-ID-Platform-Connection');
  const [userContext, setUserContext] = useState({});

  /* auth functions */

  const signUp = (
    { email, mobile, password, marketingConsentPreference, ...rest },
    signUpCallback,
  ) => {
    setIsLoading(true);

    auth0Client.redirect.signupAndLogin(
      {
        connection: databaseConnection,
        ...(rest.name && { name: rest.name }),
        email,
        password,
        user_metadata: {
          country_code: '65',
          phone_number: `${mobile}`,
          marketing_consent_preference: marketingConsentPreference.toString(),
        },
      },
      (error, result) => {
        if (error) {
          const derivedError = getErrorFromDesc(error);
          datadogRum.addError(derivedError);
        }
        setIsLoading(false);
        if (signUpCallback) {
          signUpCallback(error, result);
        }
      },
    );
  };

  const login = ({ email, password }, loginCallback) => {
    setIsLoading(true);
    auth0Client.login(
      {
        realm: databaseConnection,
        email,
        password,
      },
      (error, result) => {
        setIsLoading(false);
        if (error) {
          const derivedError = getErrorFromDesc(error);
          datadogRum.addError(derivedError);
        }
        if (loginCallback) {
          loginCallback(error, result);
        }
      },
    );
  };

  const socialSignIn = (serviceName) => {
    setIsLoading(true);
    const socialSignInOptions = {
      connection: serviceName,
    };
    const generatedNonce = Buffer.from(new Date().getTime().toString()).toString('base64');
    if (serviceName === 'singpass') {
      socialSignInOptions.login_hint = auth0ConfigProps.extraParams.nonce || generatedNonce;
    }

    auth0Client.authorize(socialSignInOptions);
  };

  const forgotPassword = ({ email }, resetCallback) => {
    setIsLoading(true);
    auth0Client.changePassword(
      {
        connection: databaseConnection,
        email,
      },
      (error, result) => {
        setIsLoading(false);
        if (error) {
          const derivedError = getErrorFromDesc(error);
          datadogRum.addError(derivedError);
        }
        if (resetCallback) {
          resetCallback(error, result);
        }
      },
    );
  };

  const passwordlessStart = ({ phoneNumber }, resetCallback) => {
    auth0Client.passwordlessStart(
      {
        connection: 'ned-sms',
        send: 'code',
        phoneNumber: `+65${phoneNumber}`,
      },
      (error, result) => {
        if (error) {
          const derivedError = getErrorFromDesc(error);
          datadogRum.addError(derivedError);
        }
        if (resetCallback) {
          resetCallback(error, result);
        }
      },
    );
  };

  const passwordlessLogin = ({ phoneNumber, verificationCode }, resetCallback) => {
    auth0Client.passwordlessLogin(
      {
        connection: 'ned-sms',
        verificationCode,
        phoneNumber: `+65${phoneNumber}`,
      },
      (error, result) => {
        if (error) {
          const derivedError = getErrorFromDesc(error);
          datadogRum.addError(derivedError);
        }
        if (resetCallback) {
          resetCallback(error, result);
        }
      },
    );
  };

  const resetPassword = (values, userCallback) => {
    setIsLoading(true);
    PasswordReset(values, (error, returnAppLoginUrl) => {
      setIsLoading(false);
      // we are not sending errors to Datadog here
      // as it has been done at API level (catch block of POST request)
      if (userCallback) {
        userCallback(error, returnAppLoginUrl);
      }
    });
  };

  /* user context */

  const resetUserContext = () => {
    setUserContext((oldContext) => _pick(oldContext, USER_CONTEXT_NO_RESET));
  };

  const updateUserContext = React.useCallback((newValue = {}) => {
    setUserContext((oldContext) => ({ ...oldContext, ...newValue }));
  }, []);

  const stateValue = React.useMemo(
    () => _get(auth0ConfigProps, 'extraParams.state'),
    [auth0ConfigProps],
  );

  /* tracking */

  const trackPwl = React.useCallback(
    (dynamicParams = {}) => {
      const commonParams = {
        /* Common params */
        event: 'custom_event',
        event_category: 'SSO',
        state: stateValue,
        client_id: auth0ConfigProps.clientID,
        referrer: document.referrer,
        environment: getEnv,
        scid_auth_ref: _get(auth0ConfigProps, 'extraParams.scid_auth_ref', 'nil'),
      };

      TagManager.dataLayer({
        dataLayer: {
          ...commonParams,
          ...dynamicParams,
          /* Dynamic params - refer to tag plan sheet */
          // screen_name,
          // event_action,
          // event_label,
          // origin_target: {e.target.name}, // for inputs only
          // checkbox: '{checkbox name}-{checked/unchecked}',
        },
      });
    },
    [auth0ConfigProps, stateValue],
  );

  /* redirect error */

  const getRedirectErrorShownCookie = React.useCallback(
    () => JsCookies.get(COOKIE_REDIRECT_ERROR_SHOWN_KEY),
    [],
  );

  const shouldShowRedirectError = React.useMemo(
    () => userContext.redirectError && getRedirectErrorShownCookie() !== stateValue,

    [userContext, getRedirectErrorShownCookie, stateValue],
  );

  const setRedirectErrorShownCookie = React.useCallback(() => {
    JsCookies.set(COOKIE_REDIRECT_ERROR_SHOWN_KEY, stateValue, {
      expires: 365,
    });
  }, [stateValue]);

  /* init */

  const initAuth0 = React.useCallback(() => {
    if (!auth0ConfigProps) {
      return;
    }

    const config = auth0ConfigProps;
    const responseType =
      (config.internalOptions || {}).response_type ||
      (config.callbackOnLocationHash ? 'token' : 'code');

    const params = {
      /* additional configuration needed for use of custom domains */
      overrides: {
        __tenant: config.auth0Tenant,
        __token_issuer: config.authorizationServer.issuer,
      },
      domain: config.auth0Domain,
      clientID: config.clientID,
      redirectUri: config.callbackURL,
      responseType,
      ...config.internalOptions,
    };

    setIsLoading(true);

    const Auth0Client = new Auth0.WebAuth(params);

    setAuth0Client(Auth0Client);

    setDatabaseConnection('NE-Digital-ID-Platform-Connection');

    setIsLoading(false);
  }, [auth0ConfigProps]);

  const fetchCustomParams = React.useCallback(() => {
    if (!auth0ConfigProps) {
      return;
    }

    const config = auth0ConfigProps;

    // entry source
    const authRefParam = _get(config, 'extraParams.scid_auth_ref', '');

    // email prefill
    let handoffEmailParam = _get(config, 'extraParams.handoff_email', '');
    if (handoffEmailParam) {
      handoffEmailParam = window.atob(handoffEmailParam);
    }

    // client-based UI: client name
    let clientName = _get(EnvVars, ['CLIENT_MAPPING', config.clientID, 'service_name'], '');
    if (!clientName) {
      clientName = _get(config, 'dict.signin.title');
    }

    // client-based UI: marketing consent
    const marketingConsentText = _get(
      EnvVars,
      ['CLIENT_MAPPING', config.clientID, 'marketing_consent'],
      DEFAULT_TNC,
    );

    // client-based UI: theme color by client
    const clientService = _get(EnvVars, ['CLIENT_MAPPING', config.clientID, 'service'], 'link');
    const themeColorName = _get(CLIENT_COLOR_NAMES, clientService);

    // SE name
    const seName = _get(SE_NAMES, clientService, clientName);

    // terms and conditions, privacy policy/notice
    const tncLink = _get(SE_ADDITIONAL_LEGALS, [clientService, 'tnc_link']);
    const privacyLink = _get(SE_ADDITIONAL_LEGALS, [clientService, 'privacy_link']);

    // client-based UI: custom logo
    const customLogo = clientService === 'ntuc' ? 'ntuc' : 'link';

    // error from social sign in
    const redirectError = _get(config, 'extraParams.redirect_error', false);

    // Singpass user re-authentication
    const isSingpassReAuth = _get(config, 'extraParams.login_hint', false) === 'singpass-reauth';

    updateUserContext({
      ...(authRefParam && { authRef: authRefParam }),
      ...(handoffEmailParam && { prefillEmail: handoffEmailParam }),
      clientBasedUi: {
        clientService,
        seName: seName,
        welcomeName: clientName,
        marketingConsent: marketingConsentText,
        tncLink,
        privacyLink,
        themeColorName,
        customLogo,
      },
      ...(redirectError && { redirectError }),
      isSingpassReAuth,
    });
  }, [auth0ConfigProps, updateUserContext]);

  const initSingpass = React.useCallback(() => {
    if (!auth0ConfigProps) {
      return;
    }

    const config = auth0ConfigProps;

    const authParamsSupplier = async () => ({
      state: config.extraParams.ndi_state,
      nonce: config.extraParams.ndi_nonce,
    });

    const onError = (errorId, message) => {
      console.log('[NDI][initAuthSession] error', `errorId: ${errorId}, message: ${message}`);
    };

    const initAuthSessionResponse = window.NDI.initAuthSession(
      'linkpass-sso-sp',
      {
        clientId: SINGPASS_CLIENT_ID,
        redirectUri: SINGPASS_REDIRECT_URL,
        scope: 'openid',
        responseType: 'code',
      },
      authParamsSupplier,
      onError,
      {
        renderDownloadLink: true,
      },
    );

    console.log('[NDI][initAuthSession] res', initAuthSessionResponse);
  }, [auth0ConfigProps]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted && typeof auth0ConfigProps !== 'undefined') {
      // auth0Config will be undefined on the initial load (pre-rendered)
      // we make use of React.useEffect hook to listen to the change of auth0Config
      // and therefore initialize initAuth0 on subsequent client-side rerender
      initAuth0();
      // fetch custom params from extraParams and pass to user context
      fetchCustomParams();
      // custom param for Singpass
      updateUserContext({
        isSingpassLogin: auth0ConfigProps.extraParams.singpass,
      });
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Auth0Context.Provider
      value={{
        isLoading,
        signUp,
        login,
        socialSignIn,
        forgotPassword,
        passwordlessStart,
        passwordlessLogin,
        resetPassword,
        auth0RPConfig,
        userContext,
        resetUserContext,
        updateUserContext,
        trackPwl,
        shouldShowRedirectError,
        setRedirectErrorShownCookie,
        initSingpass,
      }}
    >
      <>{children}</>
    </Auth0Context.Provider>
  );
};

Auth0Provider.propTypes = {
  children: PropTypes.node.isRequired,
  auth0Config: PropTypes.object,
  auth0RPConfig: PropTypes.object,
};

Auth0Provider.defaultProps = {
  auth0Config: undefined,
  auth0RPConfig: undefined,
};

export default Auth0Provider;
